/**
 * An alert is a fact, and facts get rows.
 *
 * CLAUDE.md section 0: "a degraded system must be loud to the operator and
 * graceful to the customer, never the reverse". The gateway has been loud into
 * a `string[]` on the heap, which is loud only to whoever is attached to that
 * process at that moment. A worker that degrades its models at 2pm and is
 * redeployed at 3pm has destroyed the record that it happened.
 *
 * So alerts leave through a port. The sink writes them wherever the deployment
 * keeps its append-only log; the strings stay as the in-process view.
 */

/** Who raised it. Section 5: every row in this system is attributable. */
export interface Alert {
  /** Machine name: `budget_blocked`, `model_degraded`, `task_dead_letter`. */
  readonly kind: string;
  /**
   * What makes this alert THIS alert.
   *
   * Two raises with the same key are the same fact and land as one row. The
   * key is therefore a design decision per kind, not a formality:
   *
   * - A standing fact about a day or a configuration keys on that day or that
   *   configuration, so a restart does not re-announce it and a cap crossed
   *   again tomorrow is heard again.
   * - A discrete occurrence has no identity but itself, so it carries a fresh
   *   id. Deduping an injection attempt would file an attack as a
   *   configuration fact and lose the count entirely.
   *
   * Never customer text (section 10). `PgAlertSink` prints the kind and the
   * key to stderr once per fact (TA-293), so a key lands in a log group as
   * well as in the ledger: ids, days, codes and names of things, never what
   * a customer wrote or who they are. The key is also what that line is
   * deduped on, as the row is.
   */
  readonly key: string;
  /** One line an operator can act on without opening anything else. */
  readonly message: string;
  readonly at: Date;
  /**
   * Omitted only by alerts that are about the deployment rather than a client,
   * such as a provider's breaker tripping. The sink supplies its own client in
   * that case, which is honest while F-17 holds ("client_id on every row,
   * single-instance v1").
   */
  readonly clientId?: string;
  readonly agentId?: string;
  readonly agentVersion?: string;
  /** Structured, queryable, and never customer text (section 10). */
  readonly detail?: Readonly<Record<string, unknown>>;
  readonly contactId?: string;
  readonly threadId?: string;
}

export interface AlertSink {
  /**
   * False means alerts die with the process that raised them. The startup
   * banner reads this for the same reason it reads `budgetDurable`.
   */
  readonly durable: boolean;
  raise(alert: Alert): Promise<void>;
}

/**
 * The default, and a lie in production for exactly the reason above.
 *
 * It does not dedupe. Deduplication belongs to the key and to the store's
 * unique index, which is a DB-level backstop rather than an application-level
 * one, the same choice the queue made under F-24.
 */
export class InMemoryAlertSink implements AlertSink {
  readonly durable = false;
  readonly raised: Alert[] = [];

  async raise(alert: Alert): Promise<void> {
    this.raised.push(alert);
  }

  clear(): void {
    this.raised.length = 0;
  }
}

/**
 * Decision list item 23, Kabir 2026-09-27: page on refusals and reds; amber
 * goes to the daily digest.
 *
 * Until now every alert paged (OFK row 11), so a draft waiting an hour mailed
 * the operator exactly as a hard budget block did. The split is this named
 * list, and everything NOT on it pages. A kind added next month, a kind built
 * at runtime (`drift_*`, a dead-lettered task's own kind) and a misspelt entry
 * here all land on the loud side (section 0).
 *
 * - `escalation_amber`: TA-180's team-board amber, a draft waiting over an
 *   hour. The red step, a holding message sent or withheld, still pages.
 * - `appointment_attendance_needed`: a time passed with no attendance
 *   recorded. A bookkeeping to-do for a human.
 * - `graduation_eligible`: B6, thresholds qualify and a human flips the
 *   switch. Nothing moved; an opportunity, not a fault.
 * - `foreign_echo_superseded_drafts`: TA-612 (go-live judge 14, ruled by
 *   Fable under Kabir's delegation), an echo not sent by the agent
 *   superseded pending drafts (packages/db ingest.ts).
 *
 * Frozen, so nothing at runtime can edit it into paging less. Every amber fact
 * is still a row in the ledger; only the page is deferred.
 */
export const AMBER_ALERT_KINDS: readonly string[] = Object.freeze([
  "escalation_amber",
  "appointment_attendance_needed",
  "graduation_eligible",
  // TA-612: in draft mode every hand reply in the Meta inbox supersedes a draft, so a page would fire on every owner answer.
  "foreign_echo_superseded_drafts",
  // V1 STAGES: an Engaged lead's 24h qualify clock ran out. Goes to the digest, never pages. A New
  // breach writes the event only, because F-06's draft-latency check already pages at 2 minutes.
  "stage_sla_breached",
  // V1 STAGES: the daily pipeline hygiene count (undated, unowned, not derivable) is above 0. One a day.
  "pipeline_hygiene",
  // V6 SUPPRESS: a bereavement or classifier-confirmed opt-out is a suggestion the owner acts on with one tap.
  "suppression_suggested",
  // TA-1361: an appointment reminder that will not leave the building (withheld at a gate that will not clear for
  // its tier, or cleared with no transmitter for fixed lines), so the owner reminds the customer by hand. Owner news,
  // not a fault; one per appointment and tier, ids and times only (packages/agents appointmentReminderSweep.ts).
  "appointment_reminder_needs_hand",
]);

/**
 * V14 review. The owner's news, on a route of its own: neither a fault nor
 * amber, and not quieter than a page, only separate from it.
 *
 * - `vip_inbound`: a contact the owner tagged VIP wrote (packages/db
 *   vipInbound.ts). It printed `[alert]` like a fault, and the paging alarm
 *   counts that phrase over five minutes and mails no second time while in
 *   ALARM, so a VIP writing every few minutes held it there and a budget
 *   block or a dead letter in that time mailed nobody (section 0). The sweep
 *   failing (`vip_inbound_sweep_failed`) is a fault and still pages.
 *
 * - `vip_detected` (U2 VIP, Agent Brains 5.1 "vip.detected pings the owner"):
 *   the research step read a contact as a likely VIP. Ids, the pseudonym and
 *   signal codes only. Like `vip_inbound` it is news for the owner, not a
 *   fault: the route addition is the U2 design's, not part of the 2026-10-02
 *   ratification quoted below, which named `vip_inbound` alone.
 *
 * - `consult_proposed` and `consult_brief_ready` (V3 SCHEDULER): the scheduler
 *   offers slots for the owner to book, and a consult brief is ready. News for
 *   the owner to act on, not faults; ids and counts only. Like `vip_detected`
 *   the addition is the V3 design's, not part of the ratification below.
 *   `consult_book_failed` and `consult_brief_failed` are faults and page.
 *
 * - `consult_confirmation_withheld` (V3 SCHEDULER fix 1): the booking stands but
 *   its confirmation line did not go out (a gate held it, or the owner has not
 *   said the time was agreed with the customer), so the owner confirms by hand.
 *   News for the owner to act on; ids and a gate code only.
 *
 * Frozen, as the amber list is, so nothing at runtime can route a fault here.
 *
 * The ratified route, exactly `vip_inbound`, and its numbers (the
 * sweep cadence in apps/worker vipInboundSweep.ts, the lookback, batch and
 * skip horizon in packages/db vipInbound.ts, the alarm period in
 * deploy/aws/alarms.tf): RATIFIED by Kabir 2026-10-02 (decision list item 121,
 * typed reply "Yes I want to continue the production" to the accept-the-recs
 * question). The channel stays the ops alerts topic in the pilot
 * (deploy/aws/alarms.tf). Whether the owner's phone must ring inside the
 * minute, which needs a direct publish, is not part of that and stays open,
 * for the owner interview.
 */
export const OWNER_ALERT_KINDS: readonly string[] = Object.freeze([
  "vip_inbound",
  "vip_detected",
  "consult_proposed",
  "consult_brief_ready",
  "consult_confirmation_withheld",
  // X4 TEAM: a person-to-person handoff note went unacknowledged past the ack SLA. News for the owner (a customer may be waiting
  // on a colleague who is away), not a system fault, so it does not page; ids and numbers only. Not part of the ratification above.
  "dropped_baton_human",
  // TA-1361: priced facts in the KB the worker serves are past their date (freshness.ts kbExpiry), so price questions
  // become "let me check" turns. The owner re-confirms them on Shop answers; nothing is broken. One per client per UTC
  // day, ids and counts only. Not part of the ratification above.
  "kb_price_stale",
]);

export type AlertRoute = "page" | "amber" | "owner";

/** A kind is matched whole and case-sensitive. Anything else pages. */
export function alertRoute(kind: string): AlertRoute {
  if (AMBER_ALERT_KINDS.includes(kind)) return "amber";
  if (OWNER_ALERT_KINDS.includes(kind)) return "owner";
  return "page";
}
